import React from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import cn from '@/utils/cn';
import apiErrorMessage from './apiErrors';
import SPAButton from './SPAButton';

// The pieces every edit section shares (program and unit edit pages): the raised panel, its field
// anatomy, the Save footer, and the loading skeleton.

export const panelClass = cn(
  'tw-box-border tw-rounded-lg tw-border tw-border-solid tw-border-spa-border-secondary',
  'tw-bg-spa-bg-secondary tw-p-6',
);

// Lato ships at 400 and 700 only (layouts/_fonts), so medium would render as regular.
const labelClass = 'tw-mb-1.5 tw-block tw-text-sm tw-font-bold tw-text-spa-heading';
export const hintClass = 'tw-mt-1 tw-block tw-text-xs tw-text-spa-tertiary';

const RequiredMark = () => <span aria-hidden="true" className="tw-text-spa-error"> *</span>;

interface FieldProps {
  children: React.ReactNode;
  className?: string;
  error?: string;
  hint?: string;
  htmlFor: string;
  label: string;
  required?: boolean;
}

export const Field = ({
  children, className, error, hint, htmlFor, label, required = false,
}: FieldProps) => (
  <div className={cn('tw-min-w-0', className)}>
    <label className={labelClass} htmlFor={htmlFor}>
      {label}
      {required && <RequiredMark />}
    </label>
    {children}
    {hint && !error && <span className={hintClass}>{hint}</span>}
    {error && <span className="tw-mt-1 tw-block tw-text-xs tw-text-spa-error" role="alert">{error}</span>}
  </div>
);

interface PanelFooterProps {
  disabled?: boolean;
  update: Pick<UseMutationResult<unknown, unknown, unknown>, 'error' | 'isError' | 'isLoading'>;
}

export const PanelFooter = ({ disabled = false, update }: PanelFooterProps) => (
  <div
    className={cn(
      'tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-border-0 tw-border-t',
      'tw-border-solid tw-border-spa-border-secondary tw-pt-5',
    )}
  >
    {update.isError && (
      <p className="tw-m-0 tw-mr-auto tw-text-sm tw-text-spa-error" role="alert">
        {apiErrorMessage(update.error, 'Couldn’t save. Try again.')}
      </p>
    )}
    <SPAButton disabled={disabled || update.isLoading} type="submit" variant="primary">
      {update.isLoading ? 'Saving…' : 'Save Changes'}
    </SPAButton>
  </div>
);

export const PanelSkeleton = () => (
  <div aria-busy="true" className={cn(panelClass, 'tw-animate-pulse')}>
    <span className="tw-sr-only">Loading</span>
    <div className="tw-mb-6 tw-h-4 tw-w-40 tw-rounded tw-bg-spa-bg-skeleton" />
    <div className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-4 md:tw-grid-cols-2">
      {Array.from({ length: 8 }, (_, index) => (
        <div key={index}>
          <div className="tw-mb-2 tw-h-3 tw-w-24 tw-rounded tw-bg-spa-bg-skeleton-subtle" />
          <div className="tw-h-[2.125rem] tw-rounded tw-bg-spa-bg-skeleton" />
        </div>
      ))}
    </div>
  </div>
);
